import React, { useState } from "react";

import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import {
  computeAvailableActions,
  isIrreversible,
  LIFECYCLE_LABEL,
  LIFECYCLE_ORDER,
  LIFECYCLE_VARIANT,
} from "src/adminPanel/modulesV2/utils";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { Pill } from "src/base-components/Pill";
import { LifecycleState } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  title: string;
  description?: string;
  current: LifecycleState;
  desired: LifecycleState;
  /** Workspace lifecycle state for modules — the floor a module can't fall behind. Omit for workspaces. */
  floor?: LifecycleState;
  isPending?: boolean;
  /**
   * Body copy for the confirm step on a reversible target. Defaults to the
   * module wording; pass a workspace-specific renderer when reusing the modal
   * elsewhere. Irreversible targets always show the shared "cannot be undone"
   * warning regardless of this prop.
   */
  confirmDescription?: (target: LifecycleState) => React.ReactNode;
  onTransition: (target: LifecycleState) => void | Promise<void>;
};

export const LifecycleTransitionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  title,
  description,
  current,
  desired,
  floor,
  isPending = false,
  confirmDescription,
  onTransition,
}) => {
  const [confirmTarget, setConfirmTarget] = useState<LifecycleState | null>(
    null,
  );

  const actions = computeAvailableActions({ current, desired, floor });

  const isBackward = (target: LifecycleState) =>
    LIFECYCLE_ORDER.indexOf(target) < LIFECYCLE_ORDER.indexOf(current);

  // Every state press is a deliberate two-step action: pick the target, then
  // confirm. Irreversible targets get a stronger warning at the confirm step.
  const handleTargetClick = (target: LifecycleState) => {
    setConfirmTarget(target);
  };

  return (
    <>
      <Modal afterLeave={afterLeave} open={isOpen} size="sm" onClose={onClose}>
        <Modal.Header description={description}>{title}</Modal.Header>
        <Modal.Content>
          <div className="space-y-4">
            <div className="flex items-center gap-2 font-inter-normal-13px text-gray-600">
              <span>Current state</span>
              <Pill variant={LIFECYCLE_VARIANT[current]}>
                <Pill.Text fontType="text">
                  {LIFECYCLE_LABEL[current]}
                </Pill.Text>
              </Pill>
            </div>

            {actions.status === "in_flight" ? (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 font-inter-normal-13px text-yellow-800">
                A transition to{" "}
                <Pill variant={LIFECYCLE_VARIANT[actions.desired]}>
                  <Pill.Text fontType="text">
                    {LIFECYCLE_LABEL[actions.desired]}
                  </Pill.Text>
                </Pill>{" "}
                is in progress. Wait for it to complete before changing the
                lifecycle again.
              </div>
            ) : actions.targets.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 font-inter-normal-13px text-gray-500">
                No lifecycle transitions are available from this state.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                  Move to
                </div>
                <div className="flex flex-col gap-2">
                  {actions.targets.map((target) => (
                    <button
                      key={target}
                      className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isPending}
                      type="button"
                      onClick={() => handleTargetClick(target)}
                    >
                      <Icon
                        icon={isBackward(target) ? faArrowLeft : faArrowRight}
                        size="xs"
                      />
                      <Pill variant={LIFECYCLE_VARIANT[target]}>
                        <Pill.Text fontType="text">
                          {LIFECYCLE_LABEL[target]}
                        </Pill.Text>
                      </Pill>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Content>
      </Modal>

      {isOpen && confirmTarget !== null && (
        <ConfirmationModal
          confirmationButtonText={`Move to ${LIFECYCLE_LABEL[confirmTarget]}`}
          description={
            isIrreversible(confirmTarget) ? (
              <>
                Moving to <strong>{LIFECYCLE_LABEL[confirmTarget]}</strong>{" "}
                cannot be undone. The lifecycle has no path back to Active from
                here.
              </>
            ) : confirmDescription ? (
              confirmDescription(confirmTarget)
            ) : (
              <>
                This queues an operation to move the module to{" "}
                <strong>{LIFECYCLE_LABEL[confirmTarget]}</strong>.
              </>
            )
          }
          open={confirmTarget !== null}
          title={`Move to ${LIFECYCLE_LABEL[confirmTarget]}?`}
          variant={isIrreversible(confirmTarget) ? "danger" : "default"}
          onClose={() => setConfirmTarget(null)}
          onConfirm={async () => {
            await onTransition(confirmTarget);
            setConfirmTarget(null);
          }}
        />
      )}
    </>
  );
};
